import React, { FC, FocusEvent, useState, useEffect, ChangeEvent } from "react";
import classNames from "classnames";
import { UseFormRegister } from "react-hook-form";

import { FieldErrorPosition, FieldsSize } from "../../Helpers/interfaces";

export type InputProps = {
  label: string;
  name: string;
  error: any;
  register: UseFormRegister<any>;
  getValues: any;
  size?: FieldsSize;
  required?: boolean;
  reversedColors?: boolean;
  inputmode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
  type?: "text" | "number" | "email" | "password" | "tel";
  pattern?: string;
  autocomplete?: string;
  errorPosition?: FieldErrorPosition;
  autoFocus?: boolean;
  className?: string;
};

export const Input: FC<InputProps> = ({
  label,
  name,
  register,
  getValues,
  error,
  size = FieldsSize.fullWidth,
  required = false,
  reversedColors = false,
  type = "text",
  inputmode = "text",
  autocomplete = "",
  pattern = undefined,
  errorPosition = FieldErrorPosition.left,
  autoFocus,
  className = "",
}) => {
  const [hasValue, setHasValue] = useState<boolean>(false);

  const id = `input-${name}`;
  const hasError = error != undefined;
  const { onBlur, onChange, ...registerRest } = register(name);

  useEffect(() => {
    if (getValues) {
      setHasValue(!!getValues(name));
    }
  }, []);

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setHasValue(!!e.target.value);
    onBlur(e);
  };

  const handleChange = (e: ChangeEvent) => {
    setHasValue(true);
    onChange(e);
  };

  return (
    <div className={`position-relative booking-narrow-item ${className}`}>
      <label
        className={classNames("d-block position-relative my-2", {
          "form-group--fullWidth": size == FieldsSize.fullWidth,
          "form-group--halfWidth": size == FieldsSize.halfWidth,
          "form-group--twoThirdsWidth": size == FieldsSize.twoThirdsWidth,
          "form-group--small": size == FieldsSize.oneThirdWidth,
          "input--reversed": reversedColors,
          "input--error": hasError,
        })}
      >
        <input
          {...registerRest}
          autoFocus={autoFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          id={id}
          name={name}
          type={type}
          inputMode={inputmode ? inputmode : "text"}
          autoComplete={autocomplete ? autocomplete : ""}
          pattern={pattern}
          required={required}
          aria-describedby={"errorId-" + id}
          className="form-control mb-0"
        />
        <span
          className={classNames("input__label", {
            "input__label--focus": hasValue,
          })}
        >
          {label} {required}
        </span>
      </label>
      {error && error.message && (
        <div
          className={classNames("input__support p-1 mt-n2 mb-2", {
            "input__support--error": hasError,
            "input__support--errorLeft":
              errorPosition == FieldErrorPosition.left,
            "input__support--errorBottom":
              errorPosition == FieldErrorPosition.bottom,
          })}
        >
          <p id={"errorId-" + id} className="fs-6 input__supportText">
            {error.message}
          </p>
        </div>
      )}
    </div>
  );
};
